// inlineModelAdapter — the buffer adapters behind `commitEdit`: the Monaco
// editor adapter (compact popover + the Query Editor's Visual mode) and the
// string-splice model adapter every surface uses as its `ctx.model`.

import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import type {
  MonacoEditorLike,
  MonacoSingleEditOperation,
  MonacoTextModelLike,
} from '../edit/commitEdit';

/**
 * Editor adapter for the Monaco-backed commit path. Backs `commitEdit`'s
 * `ctx.editor` branch with the real Monaco `executeEdits`, so an edit lands as
 * a SINGLE undo stop on the Query Editor's own undo stack (Cmd/Ctrl+Z undoes
 * the visual edit AND the user's own prior typing stays undoable) — unlike the
 * whole-document `setValue` fallback in the model adapter, which resets the
 * model's undo history (P12 Task 4, decision 5).
 *
 * INVARIANT: the ranges `commitEdit` feeds here are derived by the model
 * adapter's `getPositionAt`, which walks the PARSED source. Task 4's pre-resolve
 * flush guarantees parsed === live at splice time, so those (line, column)
 * positions line up with the real Monaco model. If the editor handle is gone
 * (editor unmounted), `executeEdits` no-ops via `?? false` and `commitEdit`'s
 * own live-source guard would already have refused the commit.
 */
export function createInlineEditorAdapter(
  editorRef: React.RefObject<SqlMonacoHandle | null>,
): MonacoEditorLike {
  return {
    executeEdits(source, edits) {
      return editorRef.current?.executeEdits?.(source, edits) ?? false;
    },
  };
}

/** Model adapter whose fallback apply rewrites the whole Monaco value. */
export function createInlineModelAdapter(
  editorRef: React.RefObject<SqlMonacoHandle | null>,
  getSource: () => string,
): MonacoTextModelLike {
  return createSourceModelAdapter(getSource, (next) => {
    editorRef.current?.setValue(next);
  });
}

/**
 * `commitEdit` model adapter over a plain string buffer: positions are walked
 * on `getSource()` (always the latest text), and `pushEditOperations` splices
 * the edits and hands the result to `write`.
 */
export function createSourceModelAdapter(
  getSource: () => string,
  write: (next: string, previous: string) => void,
): MonacoTextModelLike {
  return {
    getPositionAt(offset) {
      return positionAtOffset(getSource(), offset);
    },
    pushEditOperations(_before, edits) {
      const source = getSource();
      write(applyEditsSource(source, edits), source);
      return null;
    },
  };
}

/** 1-based Monaco (line, column) of a 0-based offset in `source` (clamped to its length). */
export function positionAtOffset(source: string, offset: number): { lineNumber: number; column: number } {
  const before = source.slice(0, offset);
  return { lineNumber: before.split('\n').length, column: before.length - before.lastIndexOf('\n') };
}

/** 0-based offset of a 1-based Monaco (line, column) in `source`. */
function offsetAtPosition(source: string, line: number, column: number): number {
  let lineStart = 0;
  for (let l = 1; l < line; l++) {
    const newline = source.indexOf('\n', lineStart);
    if (newline < 0) return source.length;
    lineStart = newline + 1;
  }
  return Math.min(lineStart + column - 1, source.length);
}

/**
 * Apply range edits to a string buffer. Resolves every (line, column) against
 * the ORIGINAL source up front, then splices right-to-left so each splice's
 * offsets stay valid against the partially-spliced text (Monaco's own model
 * does the same internally).
 */
export function applyEditsSource(
  source: string,
  edits: ReadonlyArray<MonacoSingleEditOperation>,
): string {
  const resolved = edits.map((e) => ({
    start: offsetAtPosition(source, e.range.startLineNumber, e.range.startColumn),
    end: offsetAtPosition(source, e.range.endLineNumber, e.range.endColumn),
    text: e.text,
  }));
  resolved.sort((a, b) => b.start - a.start);
  let out = source;
  for (const e of resolved) {
    out = out.slice(0, e.start) + e.text + out.slice(e.end);
  }
  return out;
}
